import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { 
  Trophy, TrendingUp, Users, Target, MapPin, 
  Calendar, Edit, Loader2, Eye, Heart, MessageCircle,
  Award, FileText, Clock, Briefcase
} from 'lucide-react';
import { dashboardAPI } from '../../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const AthleteDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editStatsModal, setEditStatsModal] = useState(false);
  const [editableStats, setEditableStats] = useState({});
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Load data function for refresh
  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [dashboardRes, applicationsRes] = await Promise.all([
        dashboardAPI.getAthleteDashboard(),
        dashboardAPI.getApplications()
      ]);
      setDashboardData(dashboardRes.data);
      setEditableStats(dashboardRes.data.stats || {});
      setApplications(applicationsRes.data.applications || []);
    } catch (error) {
      if (!silent) {
        console.error('Failed to load dashboard:', error);
        toast.error('Failed to load dashboard data');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Auto-refresh every 15 seconds
    const refreshInterval = setInterval(() => loadData(true), 15000);
    return () => clearInterval(refreshInterval);
  }, [loadData]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getAthleteDashboard();
      setDashboardData(response.data);
      setGoals(response.data.careerGoals || []);
      setEditableStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStats = async () => {
    try {
      await dashboardAPI.updateStats(editableStats);
      toast.success('Stats updated successfully!');
      setEditStatsModal(false);
      loadDashboard();
    } catch (error) {
      toast.error('Failed to update stats');
    }
  };

  const updateStat = (field, value) => {
    setEditableStats({ ...editableStats, [field]: value });
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'accepted': 'badge-success',
      'rejected': 'badge-danger',
      'applied': 'badge-primary',
      'pending': 'badge-warning'
    };
    return statusMap[status?.toLowerCase()] || 'badge-neutral';
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const connections = dashboardData?.connections || {};
  const visibility = dashboardData?.visibility || {};
  const opportunities = dashboardData?.opportunities || [];

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="athlete-dashboard">
      {/* Header */}
      <motion.div 
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Athlete Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Track your performance and discover opportunities
            </p>
          </div>
          <div className="flex flex-wrap gap-2 flex-shrink-0">
            <Button onClick={() => navigate('/trials')} className="btn-primary" size="sm">
              <MapPin className="w-4 h-4 mr-2" />
              Find Trials
            </Button>
            <Button onClick={() => navigate('/jobs')} variant="outline" size="sm">
              <Briefcase className="w-4 h-4 mr-2" />
              Browse Jobs
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Profile Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-5"
      >
        <Card className="card-premium overflow-hidden border-0">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <Avatar className="w-16 h-16 ring-2 ring-primary/20 ring-offset-2 ring-offset-background flex-shrink-0">
                <AvatarImage src={currentUser.profileImage} alt={currentUser.name} />
                <AvatarFallback className="text-lg font-bold bg-primary text-primary-foreground">
                  {currentUser.name?.[0] || 'A'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-foreground truncate">{currentUser.name}</h2>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <span className="inline-flex items-center text-xs text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-full">
                        <Trophy className="w-3 h-3 mr-1.5 text-primary" />
                        {currentUser.sports?.[0] || 'Multi-Sport'}
                      </span>
                      <span className="inline-flex items-center text-xs text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-full">
                        <MapPin className="w-3 h-3 mr-1.5" />
                        {currentUser.location || 'Not specified'}
                      </span>
                      <span className="inline-flex items-center text-xs font-semibold text-foreground bg-primary/10 px-2.5 py-1 rounded-full">
                        <Users className="w-3 h-3 mr-1.5 text-primary" />
                        {connections.followers || 0} followers
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditStatsModal(true)}
                    className="text-xs h-8 px-3"
                  >
                    <Edit className="w-3 h-3 mr-1.5" />
                    Edit
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Tabs */}
      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="mb-6 bg-muted/50">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="applications">
            Applications
            {applications.length > 0 && (
              <Badge className="ml-2 badge-primary">{applications.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          {/* Stats Grid */}
          <motion.div 
            className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
          >
            <motion.div variants={cardVariants}>
              <div className="stat-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="stat-label">Competitions</p>
                    <h3 className="stat-value mt-1">{stats.competitions || 0}</h3>
                  </div>
                  <div className="icon-container-purple">
                    <Trophy className="w-5 h-5 text-primary" />
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={cardVariants}>
              <div className="stat-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="stat-label">Wins</p>
                    <h3 className="stat-value mt-1">{stats.wins || 0}</h3>
                  </div>
                  <div className="icon-container-orange">
                    <Award className="w-5 h-5 text-purple-400" />
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={cardVariants}>
              <div className="stat-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="stat-label">Win Rate</p>
                    <h3 className="stat-value mt-1">
                      {stats.competitions > 0 ? Math.round((stats.wins / stats.competitions) * 100) : 0}%
                    </h3>
                  </div>
                  <div className="icon-container-blue">
                    <Target className="w-5 h-5 text-primary" />
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
            {/* Connection Tracker */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="card-premium h-full">
                <CardHeader className="pb-3 pt-4 px-5">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <Users className="w-4 h-4 text-primary" />
                    Connection Tracker
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-0">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Followers</span>
                      <span className="text-lg font-bold text-foreground">{connections.followers || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-muted-foreground">Following</span>
                      <span className="text-lg font-bold text-foreground">{connections.following || 0}</span>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full mt-4 hover-lift"
                    onClick={() => navigate('/discover')}
                  >
                    Connect with Professionals
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Visibility Insights */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <Card className="card-premium h-full">
                <CardHeader className="pb-3 pt-4 px-5">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <Eye className="w-4 h-4 text-primary" />
                    Visibility Insights
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-0">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Profile Views</span>
                      <span className="text-sm font-semibold">{visibility.profileViews || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Post Likes</span>
                      <span className="text-sm font-semibold flex items-center">
                        <Heart className="w-3.5 h-3.5 mr-1.5 text-primary" />
                        {visibility.totalLikes || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Comments</span>
                      <span className="text-sm font-semibold flex items-center">
                        <MessageCircle className="w-3.5 h-3.5 mr-1.5 text-purple-400" />
                        {visibility.totalComments || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-sm font-medium">Engagement Rate</span>
                      <span className="badge-primary">{visibility.engagement || 0} per post</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            {/* Opportunities Near You */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <Card className="card-premium h-full">
                <CardHeader className="pb-3 pt-4 px-5">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <MapPin className="w-4 h-4 text-primary" />
                    Opportunities Near You
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-0">
                  {opportunities.length > 0 ? (
                    <div className="space-y-3">
                      {opportunities.slice(0, 3).map((trial) => (
                        <div 
                          key={trial._id} 
                          className="p-4 rounded-xl bg-muted/20 hover:bg-muted/40 transition-all cursor-pointer hover-lift border border-transparent hover:border-primary/20"
                          onClick={() => navigate(`/trials/${trial._id}`)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-semibold text-foreground">{trial.title}</h4>
                            <Badge variant="outline" className="text-xs">{trial.sport}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">{trial.clubName}</p>
                          <div className="flex items-center text-xs text-muted-foreground">
                            <MapPin className="w-3 h-3 mr-1" />
                            {trial.location}
                            <Calendar className="w-3 h-3 ml-3 mr-1" />
                            {trial.date}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <MapPin className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                      <p className="text-sm text-muted-foreground mb-4">No trials found nearby</p>
                    </div>
                  )}
                  <Button className="w-full mt-4 btn-primary" onClick={() => navigate('/trials')}>
                    Browse All Trials
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

          </div>
        </TabsContent>

        <TabsContent value="applications">
          <Card className="card-premium">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="icon-container-purple">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-lg font-bold">My Applications</span>
                </div>
                <Badge className="badge-neutral">{applications.length} Total</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingApplications ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-24 bg-muted/30 animate-pulse rounded-xl" />
                  ))}
                </div>
              ) : applications.length > 0 ? (
                <div className="space-y-3">
                  {applications.map((app) => (
                    <div 
                      key={app._id}
                      className="p-4 rounded-xl bg-muted/20 hover:bg-muted/40 transition-all border border-transparent hover:border-primary/20"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-foreground">{app.title}</h4>
                            <Badge variant="outline" className="text-xs">
                              {app.type === 'trial' ? 'Trial' : 'Job'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {app.type === 'trial' ? app.clubName : app.company}
                          </p>
                          {app.location && (
                            <div className="flex items-center text-xs text-muted-foreground mt-1">
                              <MapPin className="w-3 h-3 mr-1" />
                              {app.location}
                              {app.date && (
                                <>
                                  <Calendar className="w-3 h-3 ml-3 mr-1" />
                                  {app.date}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        <span className={getStatusBadgeClass(app.status)}>{app.status || 'Pending'}</span>
                      </div>
                      {app.appliedAt && (
                        <p className="text-xs text-muted-foreground mt-2 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          Applied: {new Date(app.appliedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground mb-4">No applications yet</p>
                  <div className="flex gap-3 justify-center">
                    <Button variant="outline" className="hover-lift" onClick={() => navigate('/trials')}>
                      Browse Trials
                    </Button>
                    <Button className="btn-primary" onClick={() => navigate('/jobs')}>
                      Find Jobs
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Stats Modal */}
      <Dialog open={editStatsModal} onOpenChange={setEditStatsModal}>
        <DialogContent className="max-w-md p-0" data-testid="edit-stats-modal">
          <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 p-6 rounded-t-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-3">
                <div className="icon-container-purple">
                  <Edit className="w-5 h-5 text-primary" />
                </div>
                Edit Performance Stats
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="p-6 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold">Competitions</label>
              <Input
                type="number"
                className="input-premium"
                value={editableStats.competitions || 0}
                onChange={(e) => updateStat('competitions', parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold">Wins</label>
              <Input
                type="number"
                className="input-premium"
                value={editableStats.wins || 0}
                onChange={(e) => updateStat('wins', parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold">Goals Scored (optional)</label>
              <Input
                type="number"
                className="input-premium"
                value={editableStats.goals || 0}
                onChange={(e) => updateStat('goals', parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold">Assists (optional)</label>
              <Input
                type="number"
                className="input-premium"
                value={editableStats.assists || 0}
                onChange={(e) => updateStat('assists', parseInt(e.target.value) || 0)}
              />
            </div>
          </div>
          <DialogFooter className="p-4 border-t border-border bg-muted/30 flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setEditStatsModal(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handleSaveStats} className="btn-primary w-full sm:w-auto">
              Save Stats
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AthleteDashboard;
